import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Radio, Sparkles, AlertCircle, CheckCircle2, RefreshCw } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import Navbar from '../common/Navbar';
import Footer from '../common/Footer';
import DistributionStepper from './DistributionStepper';
import Step1ReleaseType from './Step1ReleaseType';
import Step2AudioUpload from './Step2AudioUpload';
import Step3TrackDetails from './Step3TrackDetails';
import Step4CoverArt from './Step4CoverArt';
import Step5Review from './Step5Review';
import Step6Success from './Step6Success';
import DraftRecoveryModal from './DraftRecoveryModal';
import DraftDeleteConfirmModal from './DraftDeleteConfirmModal';
import SubmissionProgressModal from './SubmissionProgressModal';
import EmailVerificationModal from '../auth/EmailVerificationModal';
import skeletonSideAsset from '../../assets/distribution/skeleton-and-note.webp';
import skeletonAudioBefore from '../../assets/distribution/skeleton and note audio upload page before upload.webp';
import skeletonAudioAfter from '../../assets/distribution/skeleton and note audio upload page after upload.webp';
import skeletonReleaseDetails from '../../assets/distribution/relesea detail page skeleton.webp';
import trackDetailSkeleton from '../../assets/distribution/track detail page skeleton.webp';
import coverArtSkeletonWebp from '../../assets/distribution/coverart page skeleton.webp';
import coverArtSkeletonAfterCoverUpload from '../../assets/distribution/coverart page skeleton after cover upload.webp';
import reviewSubmitSkeleton from '../../assets/distribution/Review and submit page skeleton.webp';
import titleArtwork from '../../assets/distribution/music-distribution-title.webp';
import {
  fetchUserDrafts,
  createNewDistributionDraft,
  updateDistributionDraft,
  saveDistributionDraft,
  deleteDistributionDraft,
  submitDistributionRelease,
  getActiveDistributionDraftId,
  setActiveDistributionDraftId,
  clearActiveDistributionDraftId,
  getEarliestReleaseDate,
  generateUUID,
  deriveReleaseYear,
  formatCLine,
  formatPLine,
} from '../../lib/distributionService';
import '../../styles/Distribution.css';

const INITIAL_RELEASE_STATE = {
  id: null,
  senritsux_registration_number: null,
  releaseType: 'single',
  title: '',
  legalName: '',
  genre: 'Pop',
  mood: 'Happy / Joyful',
  isrc: '',
  cLine: '',
  cYear: new Date().getFullYear(),
  pLine: '',
  pYear: new Date().getFullYear(),
  primaryArtists: [''],
  featuringArtists: [],
  releaseDate: getEarliestReleaseDate(),
  consumerReleaseDate: getEarliestReleaseDate(),
  originalReleaseDate: '',
  releaseTime: '00:00',
  releaseTimezone: 'Asia/Kolkata',
  socialMonetisation: {
    instagramMusic: true,
    tiktok: true,
    youtube: true,
  },
  tracks: [
    {
      title: '',
      versionType: 'Original',
      genre: 'Pop',
      mood: 'Happy / Joyful',
      language: 'Hindi',
      isrc: '',
      explicit: false,
      vocalType: 'Vocal',
      countryRecorded: 'India',
      recordingYear: new Date().getFullYear(),
      hasLyrics: true,
      lyrics: '',
      composers: [''],
      lyricists: [''],
      mixEngineer: '',
      masterEngineer: '',
      performers: [{ name: '', role: 'Vocalist' }],
      hasSamples: false,
      samples: [{ url: '', licenseUrl: '' }],
      aiUsed: false,
      aiDeclarationFile: null,
      aiDeclarationFileName: null,
    },
  ],
};

export default function DistributionPage() {
  const { user, profile, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [currentStep, setCurrentStep] = useState(1);
  const [step3View, setStep3View] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('view') === 'track-details' ? 'track-details' : 'release-info';
    } catch {
      return 'release-info';
    }
  });
  const [completedSteps, setCompletedSteps] = useState([]);
  const [savedDrafts, setSavedDrafts] = useState([]);
  const [activeDraftId, setActiveDraftId] = useState(null);
  const [showDraftRecovery, setShowDraftRecovery] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'

  const [bitDepth, setBitDepth] = useState(16);
  const [audioTracks, setAudioTracks] = useState([]);
  const [coverArt, setCoverArt] = useState(null);
  const [releaseData, setReleaseData] = useState(INITIAL_RELEASE_STATE);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionProgressState, setSubmissionProgressState] = useState({ stage: 'validating', label: 'Checking release information...' });
  const [submissionResult, setSubmissionResult] = useState(null);
  const [submissionError, setSubmissionError] = useState('');
  const [showVerifyModal, setShowVerifyModal] = useState(false);

  const isEmailVerified = Boolean(user?.email_confirmed_at || user?.confirmed_at);

  // Draft deletion confirmation state
  const [draftToDelete, setDraftToDelete] = useState(null);
  const [isDeletingDraft, setIsDeletingDraft] = useState(false);
  const [draftDeleteNotice, setDraftDeleteNotice] = useState('');
  const [draftDeleteError, setDraftDeleteError] = useState('');

  const isSavingRef = useRef(false);
  const initialLoadDoneRef = useRef(false);
  const activeDraftIdRef = useRef(null);

  // Keep activeDraftIdRef in sync
  useEffect(() => {
    activeDraftIdRef.current = activeDraftId;
  }, [activeDraftId]);

  // Compute legitimate user legal name (NEVER stage name)
  const getLegitimateLegalName = useCallback(() => {
    if (profile?.legal_name && profile.legal_name.trim()) {
      return profile.legal_name.trim();
    }
    if (
      profile?.name &&
      profile.name.trim() &&
      profile.name.trim().toLowerCase() !== (profile.stage_name || '').trim().toLowerCase()
    ) {
      return profile.name.trim();
    }
    return '';
  }, [profile]);

  // Set default primary artist and copyright lines from profile
  useEffect(() => {
    if (profile?.stage_name || profile?.name) {
      const stageName = profile.stage_name || profile.name || '';
      const legitLegal = getLegitimateLegalName();

      setReleaseData((prev) => {
        const needsPrimary = !prev.primaryArtists || prev.primaryArtists.length === 0 || prev.primaryArtists[0] === '';
        const needsLegal = !prev.legalName && legitLegal;
        const releaseYear = deriveReleaseYear(prev.releaseDate);

        if (needsPrimary || needsLegal || !prev.cLine || !prev.pLine) {
          return {
            ...prev,
            primaryArtists: needsPrimary ? [stageName] : prev.primaryArtists,
            legalName: prev.legalName || legitLegal || '',
            // Prefill © and ℗ using derived release date year; never substitute stage name for legal name
            cLine: prev.cLine || (legitLegal ? `© ${releaseYear} ${legitLegal}` : `© ${releaseYear} `),
            pLine: prev.pLine || (legitLegal ? `℗ ${releaseYear} SenritsuX / ${legitLegal}` : `℗ ${releaseYear} SenritsuX`),
          };
        }
        return prev;
      });
    }
  }, [profile, getLegitimateLegalName]);

  // Load active draft or check existing drafts on mount
  useEffect(() => {
    let isMounted = true;
    if (!user?.id || initialLoadDoneRef.current) return;

    async function initializeDraft() {
      const searchParams = new URLSearchParams(location.search);
      const urlDraftId = searchParams.get('draft_id') || searchParams.get('draft');
      const isNewRelease = searchParams.get('mode') === 'new' || searchParams.get('new') === 'true' || !urlDraftId;

      try {
        const drafts = await fetchUserDrafts(user.id);
        if (!isMounted) return;

        // Flow 1: Explicit draft resumption / editing via URL param
        if (urlDraftId && !isNewRelease) {
          const matched = drafts.find((d) => d.id === urlDraftId);
          if (matched && matched.status !== 'submitted') {
            setActiveDraftId(matched.id);
            activeDraftIdRef.current = matched.id;
            setActiveDistributionDraftId(matched.id);
            const d = matched.draftData || {};
            setReleaseData({
              ...INITIAL_RELEASE_STATE,
              ...d,
              id: matched.id,
              releaseType: d.releaseType || matched.releaseType || 'single',
            });
            if (d.audioTracks?.length) setAudioTracks(d.audioTracks);
            if (d.coverArt) setCoverArt(d.coverArt);
            const step = matched.currentStep || 1;
            setCurrentStep(step);
            const completed = [];
            for (let i = 1; i < step; i++) completed.push(i);
            setCompletedSteps(completed);
            initialLoadDoneRef.current = true;
            return;
          }
        }

        // Flow 2: Create a completely fresh, independent release
        clearActiveDistributionDraftId();
        const freshDraftId = (urlDraftId && isNewRelease) ? urlDraftId : generateUUID();
        setActiveDraftId(freshDraftId);
        activeDraftIdRef.current = freshDraftId;
        setActiveDistributionDraftId(freshDraftId);

        const legitLegal = getLegitimateLegalName();
        const stageArtist = profile?.stage_name || profile?.name || '';
        const releaseYear = deriveReleaseYear(null);

        const freshData = {
          ...INITIAL_RELEASE_STATE,
          id: freshDraftId,
          legalName: legitLegal || '',
          primaryArtists: [stageArtist],
          cLine: legitLegal ? `© ${releaseYear} ${legitLegal}` : `© ${releaseYear} `,
          pLine: legitLegal ? `℗ ${releaseYear} SenritsuX / ${legitLegal}` : `℗ ${releaseYear} SenritsuX`,
          tracks: [
            {
              ...INITIAL_RELEASE_STATE.tracks[0],
              composers: [legitLegal || ''],
              lyricists: [legitLegal || ''],
              performers: [{ name: stageArtist || '', role: 'Vocalist' }],
            },
          ],
        };

        setReleaseData(freshData);
        setAudioTracks([]);
        setCoverArt(null);
        setCurrentStep(1);
        setCompletedSteps([]);
        setShowDraftRecovery(false);

        // Update URL to assign this release its permanent unique ID
        navigate(`/distribution?draft_id=${freshDraftId}`, { replace: true });
      } catch (err) {
        console.warn('[DistributionPage] Initial draft load notice:', err);
      } finally {
        initialLoadDoneRef.current = true;
      }
    }

    initializeDraft();
    return () => {
      isMounted = false;
    };
  }, [user, location.search, getLegitimateLegalName, profile, navigate]);

  // Synchronize dynamic track detail forms with uploaded audio tracks (NEVER autofill legal name with stage name)
  useEffect(() => {
    const legitLegal = getLegitimateLegalName();
    const stageArtist = profile?.stage_name || profile?.name || '';

    setReleaseData((prev) => {
      const existingTracks = prev.tracks || [];
      const newTracksCount = audioTracks.length;

      if (newTracksCount === 0) {
        return prev;
      }

      const updatedTracks = [];
      for (let i = 0; i < newTracksCount; i++) {
        const audio = audioTracks[i];
        const existing = existingTracks[i] || {};
        const defaultTitle = audio?.filename?.replace(/\.[^/.]+$/, '') || '';

        // Composers & Lyricists must NEVER default to stage_name
        const composers = existing.composers?.length
          ? existing.composers
          : [legitLegal || ''];

        const lyricists = existing.lyricists?.length
          ? existing.lyricists
          : [legitLegal || ''];

        const performers = existing.performers?.length
          ? existing.performers
          : [{ name: stageArtist || '', role: 'Vocalist' }];

        updatedTracks.push({
          title: existing.title || defaultTitle,
          versionType: existing.versionType || 'Original',
          genre: existing.genre || prev.genre || 'Pop',
          mood: existing.mood || prev.mood || 'Happy / Joyful',
          language: existing.language || 'Hindi',
          isrc: existing.isrc || '',
          explicit: Boolean(existing.explicit),
          vocalType: existing.vocalType || 'Vocal',
          countryRecorded: existing.countryRecorded || 'India',
          recordingYear: existing.recordingYear || new Date().getFullYear(),
          hasLyrics: existing.hasLyrics !== false,
          lyrics: existing.lyrics || '',
          composers,
          lyricists,
          mixEngineer: existing.mixEngineer || '',
          masterEngineer: existing.masterEngineer || '',
          performers,
          hasSamples: Boolean(existing.hasSamples),
          samples: existing.samples || [{ url: '', licenseUrl: '' }],
          aiUsed: Boolean(existing.aiUsed),
          aiDeclarationFile: existing.aiDeclarationFile || null,
          aiDeclarationFileName: existing.aiDeclarationFileName || null,
        });
      }

      return {
        ...prev,
        tracks: updatedTracks,
      };
    });
  }, [audioTracks, profile, getLegitimateLegalName]);

  // Unified Canonical Auto-save / Update Draft Helper (Strict Single Draft Architecture)
  const handleAutoSave = useCallback(
    async (nextStep, customReleaseData = null, customAudioTracks = null, customCover = null) => {
      if (!user?.id || isSavingRef.current) return;
      isSavingRef.current = true;
      setSaveStatus('saving');

      // Canonical draft ID: strictly use this specific release instance ID
      let targetDraftId = activeDraftIdRef.current || activeDraftId || releaseData?.id;
      if (!targetDraftId) {
        targetDraftId = generateUUID();
        setActiveDraftId(targetDraftId);
        activeDraftIdRef.current = targetDraftId;
        setActiveDistributionDraftId(targetDraftId);
      }

      const dataToSave = {
        id: targetDraftId,
        currentStep: nextStep,
        releaseType: releaseData.releaseType,
        title: releaseData.title || 'Untitled Release',
        ...(customReleaseData || releaseData),
        audioTracks: customAudioTracks || audioTracks,
        coverArt: customCover || coverArt,
      };

      try {
        let saved;
        // Save or update explicitly under targetDraftId
        saved = await updateDistributionDraft(targetDraftId, user.id, dataToSave);
        if (!saved) {
          saved = await createNewDistributionDraft(user.id, dataToSave);
        }
        if (targetDraftId && location.search.indexOf(targetDraftId) === -1) {
          navigate(`/distribution?draft_id=${targetDraftId}`, { replace: true });
        }

        if (saved?.senritsux_registration_number) {
          setReleaseData((prev) => ({
            ...prev,
            senritsux_registration_number: saved.senritsux_registration_number,
          }));
        }

        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 2500);
        return saved;
      } catch (err) {
        console.warn('[DistributionPage] Auto-save error:', err);
        setSaveStatus('error');
      } finally {
        isSavingRef.current = false;
      }
    },
    [user, activeDraftId, releaseData, audioTracks, coverArt, navigate, location.search]
  );

  // Step 1 -> Step 2
  const handleSelectReleaseType = (type) => {
    setReleaseData((prev) => ({
      ...prev,
      releaseType: type,
    }));
    if (type === 'single' && audioTracks.length > 1) {
      setAudioTracks([audioTracks[0]]);
    }
  };

  const handleStep1Continue = async () => {
    if (!releaseData.releaseType) return;
    setCompletedSteps((prev) => Array.from(new Set([...prev, 1])));
    setCurrentStep(2);
    await handleAutoSave(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Step 2 -> Step 3
  const handleStep2Continue = async () => {
    if (audioTracks.length === 0) return;
    setCompletedSteps((prev) => Array.from(new Set([...prev, 1, 2])));
    setCurrentStep(3);
    setStep3View('release-info');
    await handleAutoSave(3);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Step 3 -> Step 4
  const handleStep3Continue = async () => {
    setCompletedSteps((prev) => Array.from(new Set([...prev, 1, 2, 3])));
    setCurrentStep(4);
    await handleAutoSave(4);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Step 4 -> Step 5
  const handleStep4Continue = async () => {
    if (!coverArt) return;
    setCompletedSteps((prev) => Array.from(new Set([...prev, 1, 2, 3, 4])));
    setCurrentStep(5);
    await handleAutoSave(5);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Step 5 Submit
  const handleFinalSubmit = async () => {
    setSubmissionError('');

    if (user && !isEmailVerified) {
      setShowVerifyModal(true);
      setSubmissionError('Please verify your email address before submitting a release for distribution.');
      return;
    }

    setIsSubmitting(true);
    setSubmissionProgressState({ stage: 'validating', label: 'Checking release information...' });

    try {
      const result = await submitDistributionRelease(
        user,
        {
          ...releaseData,
          draftId: activeDraftIdRef.current || activeDraftId,
          userProfile: profile,
          audioTracks,
          coverArt,
        },
        (stageInfo) => {
          setSubmissionProgressState(stageInfo);
        }
      );

      setSubmissionResult(result);
      setCurrentStep(6);
      setActiveDraftId(null);
      activeDraftIdRef.current = null;
      clearActiveDistributionDraftId();
      navigate('/distribution', { replace: true });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setSubmissionError(err.message || 'Submission could not be completed. Your draft and uploaded assets have been safely preserved.');
      // Keep existing draft safe
      handleAutoSave(5);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Resume Draft
  const handleResumeDraft = (draft) => {
    setActiveDraftId(draft.id);
    activeDraftIdRef.current = draft.id;
    setActiveDistributionDraftId(draft.id);
    setShowDraftRecovery(false);
    navigate(`/distribution?draft_id=${draft.id}`, { replace: true });

    const d = draft.draftData || {};
    setReleaseData({
      ...INITIAL_RELEASE_STATE,
      ...d,
      id: draft.id,
      releaseType: d.releaseType || draft.releaseType || 'single',
    });

    if (d.audioTracks?.length) {
      setAudioTracks(d.audioTracks);
    }
    if (d.coverArt) {
      setCoverArt(d.coverArt);
    }

    const step = draft.currentStep || 1;
    setCurrentStep(step);
    const completed = [];
    for (let i = 1; i < step; i++) completed.push(i);
    setCompletedSteps(completed);
  };

  // Delete Draft Request (Opens confirmation modal)
  const handleRequestDeleteDraft = (draft) => {
    setDraftToDelete(draft);
  };

  const handleCancelDeleteDraft = () => {
    if (!isDeletingDraft) {
      setDraftToDelete(null);
    }
  };

  const handleConfirmDeleteDraft = async () => {
    if (!draftToDelete || !user?.id || isDeletingDraft) return;

    setIsDeletingDraft(true);
    setDraftDeleteNotice('');
    setDraftDeleteError('');

    try {
      const draftId = draftToDelete.id;
      await deleteDistributionDraft(draftId, user.id);
      setSavedDrafts((prev) => prev.filter((d) => d.id !== draftId));
      setDraftDeleteNotice('Draft deleted successfully.');
      setTimeout(() => setDraftDeleteNotice(''), 4000);

      if (activeDraftId === draftId || activeDraftIdRef.current === draftId) {
        setActiveDraftId(null);
        activeDraftIdRef.current = null;
        clearActiveDistributionDraftId();
        navigate('/distribution', { replace: true });
      }

      if (savedDrafts.length <= 1) {
        setShowDraftRecovery(false);
      }
      setDraftToDelete(null);
    } catch (err) {
      console.warn('[DistributionPage] Draft delete error:', err);
      setDraftDeleteError('Unable to delete draft. Please try again.');
      setTimeout(() => setDraftDeleteError(''), 4000);
    } finally {
      setIsDeletingDraft(false);
    }
  };

  // Start New Release (Creates ONE new draft with fresh UUID)
  const handleStartNewRelease = async () => {
    setShowDraftRecovery(false);
    // Clear any previously saved draft ID so we start completely fresh
    clearActiveDistributionDraftId();
    const newDraftId = generateUUID();
    setActiveDraftId(newDraftId);
    activeDraftIdRef.current = newDraftId;
    setActiveDistributionDraftId(newDraftId);
    navigate(`/distribution?draft_id=${newDraftId}`, { replace: true });

    const legitLegal = getLegitimateLegalName();
    const stageArtist = profile?.stage_name || profile?.name || '';
    const releaseYear = deriveReleaseYear(null);

    const initialData = {
      ...INITIAL_RELEASE_STATE,
      id: newDraftId,
      legalName: legitLegal || '',
      primaryArtists: [stageArtist],
      // NEVER silently substitute stage name for legal name
      cLine: legitLegal ? `© ${releaseYear} ${legitLegal}` : `© ${releaseYear} `,
      pLine: legitLegal ? `℗ ${releaseYear} SenritsuX / ${legitLegal}` : `℗ ${releaseYear} SenritsuX`,
      tracks: [
        {
          ...INITIAL_RELEASE_STATE.tracks[0],
          composers: [legitLegal || ''],
          lyricists: [legitLegal || ''],
          performers: [{ name: stageArtist || '', role: 'Vocalist' }],
        },
      ],
    };

    setReleaseData(initialData);
    setAudioTracks([]);
    setCoverArt(null);
    setCurrentStep(1);
    setCompletedSteps([]);

    if (user?.id) {
      try {
        await createNewDistributionDraft(user.id, initialData);
      } catch (err) {
        console.warn('[DistributionPage] Start new release draft create notice:', err);
      }
    }
  };

  return (
    <div className="dist-page-container">
      <Navbar />

      <main className="dist-content-wrapper">
        {/* Delete Draft Notice / Error Banners */}
        {draftDeleteNotice && (
          <div
            style={{
              background: 'rgba(217, 251, 6, 0.12)',
              border: '1px solid rgba(217, 251, 6, 0.4)',
              borderRadius: '10px',
              padding: '0.9rem 1.2rem',
              marginBottom: '1.5rem',
              color: '#d9fb06',
              fontSize: '0.88rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              animation: 'distModalFadeIn 0.25s ease-out',
            }}
          >
            <CheckCircle2 size={16} /> {draftDeleteNotice}
          </div>
        )}

        {draftDeleteError && (
          <div
            style={{
              background: 'rgba(255, 80, 80, 0.12)',
              border: '1px solid rgba(255, 80, 80, 0.4)',
              borderRadius: '10px',
              padding: '0.9rem 1.2rem',
              marginBottom: '1.5rem',
              color: '#ff8080',
              fontSize: '0.88rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              animation: 'distModalFadeIn 0.25s ease-out',
            }}
          >
            <AlertCircle size={16} /> {draftDeleteError}
          </div>
        )}

        {/* Real Submission Multi-Step Progress Modal */}
        {isSubmitting && (
          <SubmissionProgressModal
            currentStage={submissionProgressState.stage}
            currentLabel={submissionProgressState.label}
            currentFile={submissionProgressState.currentFile}
            totalFiles={submissionProgressState.totalFiles}
            percent={submissionProgressState.percent}
          />
        )}

        {/* Unfinished Drafts Recovery Modal / Banner */}
        {showDraftRecovery && (
          <DraftRecoveryModal
            drafts={savedDrafts}
            onSelectDraft={handleResumeDraft}
            onDeleteDraft={handleRequestDeleteDraft}
            onStartNew={handleStartNewRelease}
          />
        )}
        {/* Two-Column Layout Matching 07.webp Reference */}
        <div className="dist-release-layout">
          {/* Left Column: Heading, Subtitle, and Skeleton & Notes Decoration */}
          <aside className="dist-left-column" aria-label="Music Distribution Overview">
            <div className="dist-header-text">
              <div className="dist-title-img-wrapper">
                <img
                  src={titleArtwork}
                  alt="Music Distribution"
                  className="dist-title-img"
                />
              </div>
              <p className="dist-subtitle">
                Deliver your tracks to 150+ digital streaming platforms
              </p>
            </div>

            <div className="dist-skeleton-side-container">
              <img
                src={
                  currentStep === 2
                    ? (audioTracks && audioTracks.length > 0 ? skeletonAudioAfter : skeletonAudioBefore)
                    : currentStep === 3
                    ? (step3View === 'track-details' ? trackDetailSkeleton : skeletonReleaseDetails)
                    : currentStep === 4
                    ? (coverArt && (coverArt.processedFile || coverArt.previewUrl || coverArt.url) ? coverArtSkeletonWebp : coverArtSkeletonAfterCoverUpload)
                    : currentStep === 5
                    ? reviewSubmitSkeleton
                    : skeletonSideAsset
                }
                alt={
                  currentStep === 2
                    ? (audioTracks && audioTracks.length > 0
                        ? 'Skeleton and note audio upload page after upload'
                        : 'Skeleton and note audio upload page before upload')
                    : currentStep === 3
                    ? (step3View === 'track-details'
                        ? 'Skeleton playing guitar with musical notes for track details'
                        : 'Skeleton playing guitar with musical notes for release details')
                    : currentStep === 4
                    ? (coverArt && (coverArt.processedFile || coverArt.previewUrl || coverArt.url)
                        ? 'Skeleton and note cover art page after upload'
                        : 'Skeleton and note cover art page before upload')
                    : currentStep === 5
                    ? 'Review and submit page skeleton'
                    : 'Skeleton guitarist with flowing music notes'
                }
                className={`dist-skeleton-side-decor ${
                  currentStep === 2
                    ? (audioTracks && audioTracks.length > 0 ? 'dist-skeleton-audio-after' : 'dist-skeleton-audio-before')
                    : currentStep === 3
                    ? (step3View === 'track-details' ? 'dist-skeleton-track-details' : 'dist-skeleton-release-info')
                    : currentStep === 4
                    ? (coverArt && (coverArt.processedFile || coverArt.previewUrl || coverArt.url) ? 'dist-skeleton-cover-after' : 'dist-skeleton-cover-before')
                    : currentStep === 5
                    ? 'dist-skeleton-review-submit'
                    : 'dist-skeleton-release-type'
                }`}
                data-skeleton-type={
                  currentStep === 2
                    ? (audioTracks && audioTracks.length > 0 ? 'audio-after' : 'audio-before')
                    : currentStep === 3
                    ? (step3View === 'track-details' ? 'track-details' : 'release-details')
                    : currentStep === 4
                    ? (coverArt && (coverArt.processedFile || coverArt.previewUrl || coverArt.url) ? 'cover-after' : 'cover-before')
                    : currentStep === 5
                    ? 'review-submit'
                    : 'release-type'
                }
              />
            </div>
          </aside>

          {/* Right Column: Workflow Stepper and Active Stage */}
          <section className="dist-right-column" aria-label="Distribution Workflow Steps">
            {/* Auto-Save & Draft State Badges (Positioned above stepper matching 08.webp) */}
            <div className="dist-draft-status-cluster" aria-live="polite">
              {saveStatus === 'saving' && (
                <div className="dist-draft-badge saving" style={{ background: 'rgba(255,255,255,0.08)', color: '#ffffff' }}>
                  <RefreshCw size={13} className="animate-spin" /> Saving...
                </div>
              )}
              {saveStatus === 'error' && (
                <button
                  type="button"
                  onClick={() => handleAutoSave(currentStep)}
                  className="dist-draft-badge error"
                  style={{ background: 'rgba(255,80,80,0.15)', color: '#ff8080', border: '1px solid rgba(255,80,80,0.35)', cursor: 'pointer' }}
                  title="Save failed. Click to retry saving draft."
                >
                  <AlertCircle size={13} color="#ff8080" /> Save failed (Retry)
                </button>
              )}
              {(saveStatus === 'saved' || (activeDraftId && saveStatus !== 'saving' && saveStatus !== 'error')) && (
                <div className="dist-draft-badge saved">
                  <CheckCircle2 size={14} color="#d9fb06" /> Draft Saved
                </div>
              )}
            </div>

            {/* Multi-Step Visual Progress Stepper */}
            <DistributionStepper
              currentStep={currentStep}
              completedSteps={completedSteps}
              onStepClick={(stepId) => {
                setCurrentStep(stepId);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            />

            {/* Global Submission Error Alert if any */}
            {submissionError && (
              <div
                style={{
                  background: 'rgba(255, 80, 80, 0.12)',
                  border: '1px solid rgba(255, 80, 80, 0.4)',
                  borderRadius: '12px',
                  padding: '1.2rem',
                  marginBottom: '2rem',
                  color: '#ff8080',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.6rem',
                  fontSize: '0.9rem',
                }}
              >
                <AlertCircle size={20} />
                <span>{submissionError}</span>
              </div>
            )}

            {/* Multi-Step Form Views */}
            {currentStep === 1 && (
              <Step1ReleaseType
                selectedType={releaseData.releaseType}
                onSelectType={handleSelectReleaseType}
                onContinue={handleStep1Continue}
              />
            )}

            {currentStep === 2 && (
              <Step2AudioUpload
                releaseType={releaseData.releaseType}
                audioTracks={audioTracks}
                onUpdateAudioTracks={setAudioTracks}
                bitDepth={bitDepth}
                onChangeBitDepth={setBitDepth}
                onContinue={handleStep2Continue}
                onBack={() => {
                  setCurrentStep(1);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              />
            )}

            {currentStep === 3 && (
              <Step3TrackDetails
                releaseData={releaseData}
                onUpdateReleaseData={setReleaseData}
                audioTracks={audioTracks}
                profile={profile}
                step3View={step3View}
                onStep3ViewChange={(view) => {
                  setStep3View(view);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                onContinue={handleStep3Continue}
                onBack={() => {
                  if (step3View === 'track-details') {
                    setStep3View('release-info');
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  } else {
                    setCurrentStep(2);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
              />
            )}

            {currentStep === 4 && (
              <Step4CoverArt
                coverArt={coverArt}
                onUpdateCoverArt={setCoverArt}
                onContinue={handleStep4Continue}
                onBack={() => {
                  setCurrentStep(3);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              />
            )}

            {currentStep === 5 && (
              <Step5Review
                releaseData={releaseData}
                audioTracks={audioTracks}
                coverArt={coverArt}
                profile={profile}
                onEditStep={(stepId) => {
                  setCurrentStep(stepId);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                onSubmit={handleFinalSubmit}
                isSubmitting={isSubmitting}
                isEmailVerified={isEmailVerified}
                onRequestVerification={() => setShowVerifyModal(true)}
                onBack={() => {
                  setCurrentStep(4);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              />
            )}

            {currentStep === 6 && (
              <Step6Success
                submissionResult={submissionResult}
                onNewRelease={handleStartNewRelease}
              />
            )}
          </section>
        </div>

        {/* Email Verification Modal when submission is blocked */}
        <EmailVerificationModal
          isOpen={showVerifyModal}
          email={user?.email}
          onClose={() => setShowVerifyModal(false)}
        />

        {/* Branded In-Site Draft Delete Confirmation Modal */}
        <DraftDeleteConfirmModal
          isOpen={Boolean(draftToDelete)}
          draftTitle={draftToDelete?.title || draftToDelete?.draftData?.title || 'Untitled Release'}
          isDeleting={isDeletingDraft}
          onConfirm={handleConfirmDeleteDraft}
          onCancel={handleCancelDeleteDraft}
        />
      </main>

      <Footer />
    </div>
  );
}
